'use client';

import { useTranslation } from 'next-i18next';
import { PaperClipIcon, PlusIcon } from '@heroicons/react/24/outline';
import { PopoverStyled, PopoverStyledButton, PopoverStyledPanel } from '@components/common/Popover/PopoverStyled';

interface ChatAttachMenuProps {
  /** Opens the file picker. */
  onPick: () => void;
}

/**
 * The prompt bar's `+` in chat mode: an attach menu, in the shape the mainstream assistants use.
 *
 * One entry, not one per kind. The picker takes documents and images together and the file's own
 * media type decides how the service handles it, so splitting the entry would ask the user to
 * classify a file the code already classifies. The menu stays rather than having `+` open the
 * picker directly: it names the action before taking over the screen with an OS dialog, and it is
 * where a second attachment source would go.
 *
 * Deliberately the same popover shell, trigger size and panel styling as
 * `PromptBarActionsPopup` — that one still owns the `+` in search mode, where attaching a file to
 * a keyword search would mean nothing, so the two must look like one control in two contexts.
 */
export const ChatAttachMenu = ({ onPick }: ChatAttachMenuProps) => {
  const { t } = useTranslation('common');

  return (
    <PopoverStyled className="relative">
      <PopoverStyledButton
        as="button"
        type="button"
        aria-label={t('global.label.menu')}
        className="group flex size-9 cursor-pointer items-center justify-center rounded-lg text-ink-medium transition-colors hover:bg-surface-100 hover:text-ink data-[open]:bg-surface-200 lg:size-10"
        // The textarea collapses the composer on blur; keep focus so opening the menu does not
        // fold the input shut underneath it.
        onMouseDown={(e) => e.preventDefault()}
      >
        <PlusIcon className="size-5 transition-transform duration-500 group-data-[open]:rotate-45" />
      </PopoverStyledButton>

      <PopoverStyledPanel
        animation="slideUp"
        position="left"
        // `w-max` sizes the panel to its label so it stays on one line — a 280px cap wrapped the
        // Romanian copy onto two, which reads as two entries. The cap is kept, just raised above
        // the label's natural width and bounded by the viewport so a narrow screen gets a panel
        // with a margin rather than an edge-to-edge one. Past that bound the label wraps, which is
        // the right failure.
        className="absolute bottom-full left-0 mb-2 w-max min-w-[200px] max-w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-input bg-surface p-1 shadow-lg"
      >
        <ul role="menu" className="flex flex-col">
          <li>
            <PopoverStyledButton
              as="button"
              type="button"
              role="menuitem"
              onClick={onPick}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-ink hover:bg-surface-100"
            >
              <PaperClipIcon className="size-4 shrink-0 text-ink-medium" />
              {t('bestie.chat.upload_file_hint')}
            </PopoverStyledButton>
          </li>
        </ul>
      </PopoverStyledPanel>
    </PopoverStyled>
  );
};
